import { findFirstHighlightedCalendarGroupElement } from './highlightedCalendarGroupElementIds'

const ALIGNMENT_TOLERANCE_PX = 24
const LAYOUT_STABLE_FRAMES = 6
const SCROLL_IDLE_FRAMES = 30
// About two seconds on target: rows that render lazily after the scroll still shift the row inside this window.
const ALIGNED_FRAMES_TO_FINISH = 120
const MAX_SCROLLS = 6
const DEFAULT_LOOKUP_TIMEOUT_MS = 6000
const GIVE_UP_AFTER_FALLBACK_MS = 2000
// Hard limit on the whole run, for data that never settles. The two timeouts above only count time with settled data.
const MAX_RUN_MS = 45000
// A row that has been there this long is scrolled to even if its position keeps changing by a pixel or two.
const SCROLL_DESPITE_UNSTABLE_LAYOUT_MS = 1500

export type HighlightScrollDecision =
  | { type: 'start' }
  | { type: 'waiting'; reason: 'no-row' | 'data-not-settled' | 'layout-not-stable' }
  | { type: 'row'; id: string; fallback: boolean; docTop: number; desiredScrollY: number; scrollY: number; viewportHeight: number; aligned: boolean }
  | { type: 'scroll'; top: number; behavior: ScrollBehavior; scrollsIssued: number }
  | { type: 'finish'; reason: 'stopped' | 'user-input' | 'aligned' | 'max-scrolls' | 'lookup-timeout' | 'give-up' | 'max-run' }

type ScrollToHighlightedCalendarGroupOptions = {
  /** Candidate row ids, re-read on every frame so rows that appear after the data loads are picked up. */
  getElementIds: () => string[]
  /** Best-effort row used only when none of the candidates mounts within `lookupTimeoutMs`, or as soon as `isRowHidden` says none will. */
  getFallbackElement?: () => HTMLElement | undefined
  /** True once the events have loaded and the highlighted event has no row to wait for, so the fallback is used without waiting for the timeout. */
  isRowHidden?: () => boolean
  /** False while the calendar still waits for events of the time range it was moved to. */
  isDataSettled: () => boolean
  /** Height of the sticky page header that the row has to clear. */
  headerOffset: number
  lookupTimeoutMs?: number
  /** Receives what the loop decides, for diagnostics. It must not change anything. */
  onDecision?: (decision: HighlightScrollDecision) => void
  root?: Pick<Document, 'getElementById'>
  scrollTo?: (options: ScrollToOptions) => void
  getScrollY?: () => number
  getViewportHeight?: () => number
  requestFrame?: (callback: () => void) => number
  cancelFrame?: (handle: number) => void
  now?: () => number
  listenToUserInput?: (onInput: () => void) => () => void
}

const listenToWindowUserInput = (onInput: () => void) => {
  const eventNames = ['wheel', 'touchstart', 'keydown', 'mousedown']
  eventNames.forEach((eventName) => window.addEventListener(eventName, onInput, { capture: true, passive: true }))

  return () => eventNames.forEach((eventName) => window.removeEventListener(eventName, onInput, { capture: true }))
}

/**
 * Scrolls the window so the first highlighted calendar group row sits in the middle of the viewport.
 *
 * The highlighted event is usually outside the time range the calendar has loaded: the calendar is first moved to the
 * event's dates and the events of that range, and with them the group rows and their heights, arrive later. A single
 * lookup right after the move finds no row (or a row that moves afterwards), so this keeps looking until the row
 * exists, waits for the layout to stop changing, scrolls once and then corrects the position if the layout shifts again.
 * It stops on its own after a bounded time, or as soon as the user scrolls or presses a key.
 *
 * @returns a function that stops the scroll
 */
export const scrollToHighlightedCalendarGroup = ({
  getElementIds,
  getFallbackElement,
  isRowHidden,
  isDataSettled,
  headerOffset,
  lookupTimeoutMs = DEFAULT_LOOKUP_TIMEOUT_MS,
  onDecision,
  root = document,
  scrollTo = (options) => window.scrollTo(options),
  getScrollY = () => window.scrollY,
  getViewportHeight = () => document.documentElement.clientHeight,
  requestFrame = (callback) => window.requestAnimationFrame(callback),
  cancelFrame = (handle) => window.cancelAnimationFrame(handle),
  now = () => performance.now(),
  listenToUserInput = listenToWindowUserInput,
}: ScrollToHighlightedCalendarGroupOptions): (() => void) => {
  const startedAt = now()
  let frameHandle: number | undefined
  let finished = false
  let lastDesiredScrollY: number | undefined
  let lastScrollY = getScrollY()
  let lastFrameAt = startedAt
  // Time with settled data, so a slow request for the moved-to range does not use up the time to look for the row.
  let settledMs = 0
  // Time with settled data during which only the fallback row was available. Only this budget can end the run without a scroll.
  let fallbackSettledMs = 0
  let rowMs = 0
  let lastWaitingReason: string | undefined
  let lastRowDecision: string | undefined
  let stableFrames = 0
  let idleFrames = 0
  let alignedFrames = 0
  let scrollsIssued = 0
  let lastAligned = false

  const finish = (reason: Extract<HighlightScrollDecision, { type: 'finish' }>['reason'] = 'stopped') => {
    if (finished) return
    finished = true
    if (frameHandle !== undefined) cancelFrame(frameHandle)
    stopListening()
    onDecision?.({ type: 'finish', reason })
  }

  const wait = (reason: Extract<HighlightScrollDecision, { type: 'waiting' }>['reason']) => {
    if (reason !== lastWaitingReason) onDecision?.({ type: 'waiting', reason })
    lastWaitingReason = reason
  }

  const stopListening = listenToUserInput(() => finish('user-input'))
  onDecision?.({ type: 'start' })

  const frame = () => {
    frameHandle = undefined
    if (finished) return

    const frameAt = now()
    const dataSettled = isDataSettled()
    const frameMs = frameAt - lastFrameAt
    if (dataSettled) settledMs += frameMs
    lastFrameAt = frameAt

    if (frameAt - startedAt > MAX_RUN_MS) {
      finish(lastAligned ? 'aligned' : 'max-run')
      return
    }

    const lookupTimedOut = settledMs > lookupTimeoutMs
    const candidate = findFirstHighlightedCalendarGroupElement(getElementIds(), root)
    const element = candidate ?? (lookupTimedOut || isRowHidden?.() ? getFallbackElement?.() : undefined)

    if (!element) {
      if (lookupTimedOut) finish('lookup-timeout')
      else {
        wait(dataSettled ? 'no-row' : 'data-not-settled')
        frameHandle = requestFrame(frame)
      }
      return
    }

    // The highlighted event's own row is scrolled to and corrected for as long as the run lasts. Only the fallback row,
    // which may not be where the event is, is given up on.
    if (!candidate && dataSettled) fallbackSettledMs += frameMs
    if (!candidate && fallbackSettledMs > GIVE_UP_AFTER_FALLBACK_MS) {
      finish(lastAligned ? 'aligned' : 'give-up')
      return
    }

    rowMs += frameMs

    const scrollY = getScrollY()
    const viewportHeight = getViewportHeight()
    const rowTop = element.getBoundingClientRect().top
    const desiredScrollY = Math.max(0, Math.round(rowTop + scrollY - headerOffset - viewportHeight / 2))

    // The row is scrolled to as soon as it is there and has stopped moving, also while the calendar still loads the
    // moved-to range: rows that appear later shift it, and the corrections below follow it.
    stableFrames = lastDesiredScrollY !== undefined && Math.abs(desiredScrollY - lastDesiredScrollY) <= 1 ? stableFrames + 1 : 0
    lastDesiredScrollY = desiredScrollY
    idleFrames = scrollY === lastScrollY ? idleFrames + 1 : 0
    lastScrollY = scrollY

    const aligned = Math.abs(desiredScrollY - scrollY) <= ALIGNMENT_TOLERANCE_PX
    lastAligned = aligned
    alignedFrames = aligned ? alignedFrames + 1 : 0

    const rowDecision = `${element.id}:${aligned}`
    if (rowDecision !== lastRowDecision) {
      onDecision?.({
        type: 'row',
        id: element.id,
        fallback: !candidate,
        docTop: Math.round(rowTop + scrollY),
        desiredScrollY,
        scrollY,
        viewportHeight,
        aligned,
      })
      lastRowDecision = rowDecision
    }

    const layoutStable = stableFrames >= LAYOUT_STABLE_FRAMES || rowMs > SCROLL_DESPITE_UNSTABLE_LAYOUT_MS

    if (layoutStable && !aligned && (scrollsIssued === 0 || idleFrames >= SCROLL_IDLE_FRAMES)) {
      if (scrollsIssued >= MAX_SCROLLS) {
        finish('max-scrolls')
        return
      }
      const behavior: ScrollBehavior = scrollsIssued === 0 ? 'smooth' : 'auto'
      onDecision?.({ type: 'scroll', top: desiredScrollY, behavior, scrollsIssued: scrollsIssued + 1 })
      scrollTo({ top: desiredScrollY, behavior })
      scrollsIssued++
      idleFrames = 0
    } else if (aligned && dataSettled && stableFrames >= ALIGNED_FRAMES_TO_FINISH && alignedFrames >= ALIGNED_FRAMES_TO_FINISH) {
      finish('aligned')
      return
    } else if (!aligned && !layoutStable) {
      wait('layout-not-stable')
    } else if (aligned && !dataSettled) {
      wait('data-not-settled')
    }

    frameHandle = requestFrame(frame)
  }

  frameHandle = requestFrame(frame)

  return () => finish('stopped')
}
